import { ThemeProvider } from '@mui/material';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import AuthRoute from './_components/auth/auth-route';
import { theme } from './_styles/theme.styles';
import './App.css';
import { ActionTypeIndex } from './pages/actionType/actionType-index';
import { CasesIndex } from './pages/case/case-index';
import { ClientsIndex } from './pages/client/client-index';
import { ClientPriceBandIndex } from './pages/clientPriceBand/clientPriceBand-index';
import { ClosingChecklistIndex } from './pages/closingChecklist/closingChecklist-index';
import { ClosingChecklistTypeIndex } from './pages/closingChecklistType/closingChecklistType-index';
import { DashboardIndex } from './pages/dashboard/dashboard-index';
import { FormIndex } from './pages/form/form-index';
import { FormPageManage } from './pages/form/form-pageManage';
import { FormTemplateIndex } from './pages/formTemplate/formTemplate-index';
import { FormTemplateSectionView } from './pages/formTemplate/formTemplateSection.tsx/formTemplateSection-view';
import { LogIndex } from './pages/log/log-index';
import { LoginForgotPassword } from './pages/login/login-forgotPassword';
import { LoginIndex } from './pages/login/login-index';
import { LoginResetPassword } from './pages/login/login-resetPassword';
import { LoginVerifyCode } from './pages/login/login-verifyCode';
import { TicketIndex } from './pages/ticket/ticket-index';
import { TicketsIndex } from './pages/ticket/ticket-ticketsIndex';
import { TicketActionListIndex } from './pages/ticketActionList/ticketActionList-index';
import { TicketPurgeLogIndex } from './pages/ticketPurgeLog/ticketPurgeLog-index';
import { TicketStatusIndex } from './pages/ticketStatus/ticketStatus-index';
import { UsersIndex } from './pages/users/users-index';
import { AppScreen } from './screens/app-screen';
import { AuthScreen } from './screens/auth-screen';
import { accountService } from './_services/accounts.service';
import { useEffect, useState } from 'react';
import { RoleStringEnum } from './_common/enum';
import { map, distinctUntilChanged } from 'rxjs';
import { TicketView } from './pages/ticket/ticket-view';
import { XeroIndex } from './pages/xero/xero-index';
import { TicketAgentView } from './pages/ticket/ticket-agentView';
import { TicketClientView } from './pages/ticket/ticket-clientView';
import { AllTicketsReportIndex } from './pages/reports/allTicketsReport-index';
import { AgentSummaryReportIndex } from './pages/reports/agentSummaryReport-index';
import { AgentInvoiceSummaryReportIndex } from './pages/reports/agentInvoiceSummaryReport-index';
import { PurgeTicketReportIndex } from './pages/reports/purgeTicketReport-index';


function App() {

    const [userRole, setUserRole] = useState<string | null>(null);

    useEffect(() => {
        const sub = accountService.currentUser
            .pipe(
                map(user => user?.roles?.[0]?.toString() ?? null),
                distinctUntilChanged()
            )
            .subscribe(setUserRole);

        return () => sub.unsubscribe();
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <BrowserRouter>
                <Routes>

                    <Route path="/login" element={<AuthScreen />}>
                        <Route path="" element={<LoginIndex />} />
                        <Route path="verifycode/:email" element={<LoginVerifyCode />} />
                        <Route path="forgotpassword" element={<LoginForgotPassword />} />
                        <Route path="resetpassword/:code" element={<LoginResetPassword />} />
                    </Route>

                    <Route path="/" element={<AuthRoute element={<AppScreen />} />}>

                        {(userRole === RoleStringEnum.Admin || userRole === RoleStringEnum.User) &&
                            <>
                                <Route path="/" element={<DashboardIndex />} />
                                <Route path="/cases/*" element={<CasesIndex />} />
                                <Route path="/case/:caseId/ticket/:id/:ticketType/*" element={<TicketView />} />
                                <Route path="/tickets/*" element={<TicketsIndex />} />
                                <Route path="/users/*" element={<UsersIndex />} />
                                <Route path="/Forms/:formId" element={<FormPageManage />} />
                                <Route path="/Forms" element={<FormIndex />} />
                            </>
                        }

                        {(userRole === RoleStringEnum.Admin) &&
                            <>
                                <Route path="/clients/*" element={<ClientsIndex />} />
                                <Route path="/clientPriceBands/*" element={<ClientPriceBandIndex />} />
                                <Route path="/users/*" element={<UsersIndex />} />
                                <Route path="/actionType/*" element={<ActionTypeIndex />} />
                                <Route path="/ticketActionList/*" element={<TicketActionListIndex />} />
                                <Route path="/ticketStatus/*" element={<TicketStatusIndex />} />
                                <Route path="/closingChecklist/*" element={<ClosingChecklistIndex />} />
                                <Route path="/closingChecklistItems/*" element={<ClosingChecklistTypeIndex />} />
                                <Route path="/ticketPurgeLog/*" element={<TicketPurgeLogIndex />} />
                                <Route path="/xero/*" element={<XeroIndex />} />
                                <Route path="/logs/*" element={<LogIndex />} />

                                <Route path="/FormTemplates/*" element={<FormTemplateIndex />} />
                                <Route path="/FormTemplateSections/:formTemplateSectionId/*" element={<FormTemplateSectionView />} />

                                <Route path="/agentSummaryReport" element={<AgentSummaryReportIndex />} />
                                <Route path="/agentInvoiceSummaryReport" element={<AgentInvoiceSummaryReportIndex />} />
                                <Route path="/allTicketsReport" element={<AllTicketsReportIndex />} />
                                <Route path="/purgeTicketReport" element={<PurgeTicketReportIndex />} />
                            </>
                        }

                        {(userRole === RoleStringEnum.Agent) &&
                            <>
                                <Route path="/cases/*" element={<CasesIndex />} />
                                <Route path="/tickets/*" element={<TicketsIndex />} />
                                <Route path="/case/:caseId/ticket/:id/:ticketType/*" element={<TicketAgentView />} />
                                <Route path="/Forms/:formId" element={<FormPageManage />} />
                                <Route path="/Forms" element={<FormIndex />} />
                            </>
                        }

                        {(userRole === RoleStringEnum.Client) &&
                            <>
                                <Route path="/cases/*" element={<CasesIndex />} />
                                <Route path="/case/:caseId/ticket/:id/:ticketType/*" element={<TicketClientView />} />
                                <Route path="/tickets/*" element={<TicketsIndex />} />
                                <Route path="/Forms/:formId" element={<FormPageManage />} />
                                <Route path="/Forms" element={<FormIndex />} />
                            </>
                        }

                    </Route>

                    {/*Not required but leaving here, just incase*/}
                    {/*<Route path="/SMSTemplates/*" element={<MessageSMSTemplateIndex />} />*/}
                </Routes>
            </BrowserRouter>
        </ThemeProvider >
    );
}

export default App;
